import React, { useState, useEffect, useRef } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import { usePersistedState } from '../../hooks/usePersistedState'
import DateRangeSlider from '../../components/DateRangeSlider/DateRangeSlider'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import { useClickOutside } from '../../hooks/useClickOutside'
import { TmbControlsRow, TmbStratificationPill, TmbQuestionPill, TmbPillSelect } from '../../components/TMBControls'
import {
    normalizeKey,
    safeNumber,
    findColumnIndex,
    findColumnIndexByRegex,
    parseMonthToValue,
    resolveMonthIdx,
    formatDateLabel,
    formatPercentLabel,
    COLUMN_CANDIDATES
} from '../../utils/dataUtils'
import {
    MonthInfo,
    QuestionInfo,
    HuddleTotals,
    normalizeHuddleGroup,
    generateMonthList,
    extractQuestionsFromRows,
    buildQuestionLabelMap,
    initializeSliderValues,
    getSliderIndicesFromValues,
    TMB_SLIDER_KEY,
    extractActualMonthValues,
    checkHasDataForRange
} from '../../utils/tmbUtils'
import { generatePDFIds } from '../../utils/pdfIdHelper'
import { exportToPNG, exportToSVG, exportToCSV } from '../../utils/tmbExportUtils'
import { useTmbSlider } from '../../context/TmbSliderContext'
import { applyLaunchDateFilterToZohoData } from '../../utils/tmbLaunchDates'
import '../../styles/shared/index.css'
import './AgreementInsurance.css'


const PAGE_HEADING_LINE_1 = 'Percent of Patients who "Strongly Agree"'
const PAGE_HEADING_LINE_2 = 'by Huddle Status & Insurance'

const BASE_MAX_INSURANCE_SELECTIONS = 20

const AgreementInsurance: React.FC = () => {
    const { selectedHospitalIds, refreshKey } = useHospitalSelection()
    const { globalMonthList } = useTmbSlider()
    const [rawData, setRawData] = useState<any>(null)
    const [monthList, setMonthList] = useState<MonthInfo[]>([])
    const [actualMonths, setActualMonths] = useState<Set<number>>(new Set())
    const [loading, setLoading] = useState(false)
    const [initialLoading, setInitialLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const persistenceKey = `${TMB_SLIDER_KEY}_${Array.isArray(selectedHospitalIds) ? selectedHospitalIds.join('_') : selectedHospitalIds}`
    const [sliderRange, setSliderRange] = usePersistedState<[number, number]>(persistenceKey, [0, 0])
    const [sliderStartIdx, sliderEndIdx] = getSliderIndicesFromValues(globalMonthList, sliderRange)
    const hasDataForRange = checkHasDataForRange(sliderRange[0], sliderRange[1], actualMonths)

    const [selectedQuestion, setSelectedQuestion] = usePersistedState<string>('agreement_insurance_question', '')
    const [stratification, setStratification] = usePersistedState<string>('agreement_insurance_stratification', 'ExcludeNotSure')

    const [selectedInsuranceTypes, setSelectedInsuranceTypes] = usePersistedState<string[]>('agreement_insurance_types', [])
    const [availableInsuranceTypes, setAvailableInsuranceTypes] = useState<string[]>([])

    const [insuranceDropdownOpen, setInsuranceDropdownOpen] = useState(false)
    const [questions, setQuestions] = useState<QuestionInfo[]>([])

    const insuranceDropdownRef = useRef<HTMLDivElement>(null)
    const chartContainerRef = useRef<HTMLDivElement>(null)
    const [menuOpen, setMenuOpen] = useState(false)
    const downloadMenuRef = useRef<HTMLDivElement>(null)

    const [chartCategories, setChartCategories] = useState<string[]>([])
    const [chartSeries, setChartSeries] = useState<{ name: string; data: number[] }[]>([])
    const [nLabels, setNLabels] = useState<{ yes: number[]; no: number[] }>({ yes: [], no: [] })
    const [hoverTooltip, setHoverTooltip] = useState<{ visible: boolean, x: number, y: number, content: React.ReactNode } | null>(null)
    const [isMobile, setIsMobile] = useState(false);
    const [columnIndices, setColumnIndices] = useState<{
        monthIdx: number
        questionNameIdx: number
        questionLabelIdx: number
        stratIdx: number
        huddleIdx: number
        numeratorIdx: number
        denominatorIdx: number
        insuranceIdx: number
    } | null>(null)

    const displayInsuranceLabel = (v: string) => {
        const s = String(v || "").trim()
        if (!s) return ""
        if (normalizeKey(s) === normalizeKey("Public")) return "Public"
        return s
    }

    const handleDownloadPNG = async () => {
        if (!chartContainerRef.current) return
        const filename = `Agreement_Insurance_${selectedQuestion || 'Question'}`
        const subtitle = questions.find(q => q.name === selectedQuestion)?.fullText || ''
        await exportToPNG(chartContainerRef.current, filename, 
            { title: `${PAGE_HEADING_LINE_1} ${PAGE_HEADING_LINE_2}`, subtitle },
            { cssContextClass: 'agreement-insurance-page', hideSelectors: ['.chart-subtitle'], scale: 3 }
        )
    }

    const handleDownloadCSV = () => {
        const headers = ['page', 'hospital_ids', 'stratification', 'stratification_label', 'date_start', 'date_end', 'question', 'question_text', 'huddle_group', 'insurance_type', 'numerator', 'denominator', 'percent']
        const rows: (string | number)[][] = []

        const dateStart = globalMonthList[sliderStartIdx]?.label || ''
        const dateEnd = globalMonthList[sliderEndIdx]?.label || ''

        const dateStartFormatted = formatDateLabel(dateStart)
        const dateEndFormatted = formatDateLabel(dateEnd)

        const stratLabel = stratification === 'ExcludeNotSure' ? 'Exclude "Not"' : 'Include "Not"'

        const questionCode = selectedQuestionData?.code || ''
        const questionText = selectedQuestionData?.fullText || ''

        const hospitalIds = Array.isArray(selectedHospitalIds) ? selectedHospitalIds.join(',') : selectedHospitalIds

        selectedInsuranceTypes.forEach((insType, idx) => {
            const yesVal = chartSeries[0]?.data[idx] || 0
            const noVal = chartSeries[1]?.data[idx] || 0
            const yesDenom = nLabels.yes[idx] || 0
            const noDenom = nLabels.no[idx] || 0

            const yesNum = yesDenom > 0 ? Math.round((yesVal / 100) * yesDenom) : 0
            const noNum = noDenom > 0 ? Math.round((noVal / 100) * noDenom) : 0

            rows.push([
                'agreement_insurance', hospitalIds, stratification, stratLabel,
                dateStartFormatted, dateEndFormatted, questionCode, questionText,
                'Yes Huddle', displayInsuranceLabel(insType), yesNum, yesDenom, `${yesVal}%`
            ])

            rows.push([
                'agreement_insurance', hospitalIds, stratification, stratLabel,
                dateStartFormatted, dateEndFormatted, questionCode, questionText,
                'No Huddle', displayInsuranceLabel(insType), noNum, noDenom, `${noVal}%`
            ])
        })

        exportToCSV({ headers, rows, filename: `Agreement_Insurance_${selectedQuestion || 'Question'}` })
    }

    const handleDownloadSVG = async () => {
        if (!chartContainerRef.current) return
        const filename = `Agreement_Insurance_${selectedQuestion || 'Question'}`
        const subtitle = questions.find(q => q.name === selectedQuestion)?.fullText || ''
        await exportToSVG(chartContainerRef.current, filename, 
            { title: `${PAGE_HEADING_LINE_1} ${PAGE_HEADING_LINE_2}`, subtitle },
            { cssContextClass: 'agreement-insurance-page', hideSelectors: ['.chart-subtitle'] }
        )
    }

    const handleInsuranceToggle = (insType: string, checked: boolean) => {
        if (checked) {
            if (selectedInsuranceTypes.length >= BASE_MAX_INSURANCE_SELECTIONS) {
                return
            }
            if (!selectedInsuranceTypes.includes(insType)) {
                setSelectedInsuranceTypes([...selectedInsuranceTypes, insType])
            }
        } else {
            setSelectedInsuranceTypes(selectedInsuranceTypes.filter(t => t !== insType))
        }
    }

    useEffect(() => {
        if (!selectedHospitalIds || selectedHospitalIds.length === 0) {
            setInitialLoading(false)
            setRawData(null)
            setMonthList([])
            setQuestions([])
            setError(null)
            return
        }

        const hospitalIdsParam = Array.isArray(selectedHospitalIds)
            ? selectedHospitalIds.join(',')
            : selectedHospitalIds

        setLoading(true)
        setError(null)
        setInitialLoading(true)

        const insuranceFetch = fetch(`/api/tmb/agreement_insurance/${hospitalIdsParam}`)
            .then(res => {
                if (!res.ok) {
                    if (res.status === 404) {
                        throw new Error('No data available for selected hospital(s)')
                    }
                    return res.json().then(errData => {
                        throw new Error(errData.error || `API error: ${res.status}`)
                    })
                }
                return res.json()
            })

        const singleFetch = fetch(`/api/tmb/agreement_single/${hospitalIdsParam}`)
            .then(res => {
                if (!res.ok) return null
                return res.json()
            })
            .catch(() => null)

        Promise.all([insuranceFetch, singleFetch])
            .then(([data, singleData]) => {
                applyLaunchDateFilterToZohoData(data)
                const rows = data?.response?.result?.rows || []
                const columns = data?.response?.result?.column_order || []

                if (rows.length === 0 || !columns.length) {
                    setError('No data available for selected hospital(s)')
                    setLoading(false)
                    setInitialLoading(false)
                    return
                }

                setRawData(data)

                const monthIdx = resolveMonthIdx(columns, rows)
                const questionNameIdx = findColumnIndex(columns, COLUMN_CANDIDATES.questionName) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.questionName)
                    : findColumnIndexByRegex(columns, [/questionname/])
                const questionLabelIdx = findColumnIndex(columns, COLUMN_CANDIDATES.questionLabel) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.questionLabel)
                    : findColumnIndexByRegex(columns, [/questionlabel/, /questiontext/, /question/, /itemtext/, /item/])
                const stratIdx = findColumnIndex(columns, COLUMN_CANDIDATES.stratification) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.stratification)
                    : findColumnIndexByRegex(columns, [/strat/])
                const huddleIdx = findColumnIndex(columns, COLUMN_CANDIDATES.huddle) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.huddle)
                    : findColumnIndexByRegex(columns, [/huddlegroup/, /huddle/])
                const numeratorIdx = findColumnIndex(columns, COLUMN_CANDIDATES.numerator) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.numerator)
                    : findColumnIndexByRegex(columns, [/numerator/, /num/])
                const denominatorIdx = findColumnIndex(columns, COLUMN_CANDIDATES.denominator) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.denominator)
                    : findColumnIndexByRegex(columns, [/denominator/, /denom/])
                const insuranceIdx = findColumnIndex(columns, COLUMN_CANDIDATES.insurance) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.insurance)
                    : findColumnIndexByRegex(columns, [/insurance/i])

                setColumnIndices({
                    monthIdx,
                    questionNameIdx,
                    questionLabelIdx,
                    stratIdx,
                    huddleIdx,
                    numeratorIdx,
                    denominatorIdx,
                    insuranceIdx
                })

                setActualMonths(extractActualMonthValues(rows, monthIdx))
                const months = generateMonthList(rows, monthIdx)
                setMonthList(months)

                if (months.length > 0) {
                    setSliderRange(prev => initializeSliderValues(months, prev, 12))
                }

                let labelFallbackMap: Map<string, string> | undefined
                if (singleData) {
                    const singleRows = singleData?.response?.result?.rows || []
                    const singleCols = singleData?.response?.result?.column_order || []
                    const sQnIdx = findColumnIndex(singleCols, COLUMN_CANDIDATES.questionName) !== -1
                        ? findColumnIndex(singleCols, COLUMN_CANDIDATES.questionName)
                        : findColumnIndexByRegex(singleCols, [/questionname/])
                    const sQlIdx = findColumnIndex(singleCols, COLUMN_CANDIDATES.questionLabel) !== -1
                        ? findColumnIndex(singleCols, COLUMN_CANDIDATES.questionLabel)
                        : findColumnIndexByRegex(singleCols, [/questionlabel/, /questiontext/, /question/, /itemtext/, /item/])

                    labelFallbackMap = buildQuestionLabelMap(singleRows, sQnIdx, sQlIdx)
                }

                const questionList = extractQuestionsFromRows(rows, questionNameIdx, questionLabelIdx, labelFallbackMap)
                setQuestions(questionList)
                
                if (questionList.length > 0) {
                    const selectedExists = questionList.some(q => q.name === selectedQuestion)
                    if (!selectedQuestion || !selectedExists) {
                        setSelectedQuestion(questionList[0].name)
                    }
                }

                if (insuranceIdx !== -1) {
                    const insSet = new Set<string>()
                    const defaultTypes = ["Private", "Public", "Uninsured", "Self Pay", "Not Reported"]
                    rows.forEach((r: any[]) => {
                        const val = String(r[insuranceIdx] || "").trim()
                        if (val) {
                            if (val.toLowerCase() === "selfpay" || val.toLowerCase().includes("self pay")) insSet.add("Self Pay")
                            else if (val.toLowerCase().includes("public") || val.toLowerCase() === "medicaid") insSet.add("Public")
                            else if (val.toLowerCase() === "private") insSet.add("Private")
                            else if (val.toLowerCase() === "uninsured") insSet.add("Uninsured")
                            else if (val.toLowerCase().includes("not") && val.toLowerCase().includes("reported")) insSet.add("Not Reported")
                            else insSet.add(val)
                        }
                    })
                    const foundTypes = Array.from(insSet)
                    const merged = Array.from(new Set([...defaultTypes, ...foundTypes]))
                    setAvailableInsuranceTypes(merged)

                    setSelectedInsuranceTypes(prev => prev.length === 0 ? merged : prev)
                } else {
                    const defaultList = ["Private", "Public", "Uninsured", "Self Pay", "Not Reported"]
                    setAvailableInsuranceTypes(defaultList)

                    setSelectedInsuranceTypes(prev => prev.length === 0 ? defaultList : prev)
                }

                setLoading(false)
                setInitialLoading(false)
            })
            .catch(err => {
                setError(err.message || 'Failed to load data')
                setLoading(false)
                setInitialLoading(false)
            })
    }, [selectedHospitalIds, refreshKey])

    useEffect(() => {
        if (!rawData || !columnIndices) {
            setChartCategories([])
            setChartSeries([])
            setNLabels({ yes: [], no: [] })
            return
        }

        const rows = rawData?.response?.result?.rows || []
        if (!rows.length) return

        const { monthIdx, questionNameIdx, stratIdx, huddleIdx, numeratorIdx, denominatorIdx, insuranceIdx } = columnIndices

        let filteredRows = rows

        if (monthIdx !== -1 && monthList.length > 0) {
            const minMonthVal = sliderRange[0] || 0
            const maxMonthVal = sliderRange[1] || 0
            if (minMonthVal > 0 || maxMonthVal > 0) {
                filteredRows = filteredRows.filter((r: any[]) => {
                    const v = parseMonthToValue(r[monthIdx])
                    return v >= minMonthVal && v <= maxMonthVal
                })
            }
        }

        if (selectedQuestion && questionNameIdx !== -1) {
            filteredRows = filteredRows.filter((r: any[]) => {
                return String(r[questionNameIdx] || "").trim() === selectedQuestion
            })
        }

        if (stratIdx !== -1) {
            filteredRows = filteredRows.filter((r: any[]) => {
                return normalizeKey(r[stratIdx]) === normalizeKey(stratification)
            })
        }

        const categories: string[] = []
        const yesHuddleData: number[] = []
        const noHuddleData: number[] = []
        const yesDenoms: number[] = []
        const noDenoms: number[] = []

        const insuranceTypeOrder = ['Medicaid', 'Private', 'Public', 'Medicare', 'Self Pay', 'Not Reported', 'Other']

        const sortedInsuranceTypes = [...selectedInsuranceTypes].sort((a, b) => {
            const aIndex = insuranceTypeOrder.findIndex(type => type.toLowerCase() === a.toLowerCase())
            const bIndex = insuranceTypeOrder.findIndex(type => type.toLowerCase() === b.toLowerCase())

            if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex
            if (aIndex !== -1) return -1
            if (bIndex !== -1) return 1
            return a.localeCompare(b)
        })

        sortedInsuranceTypes.forEach(insType => {
            const typeRows = filteredRows.filter((r: any[]) => {
                if (insuranceIdx === -1) return false
                const val = String(r[insuranceIdx] || "").trim().toLowerCase()
                const target = insType.toLowerCase()

                if (target === "public" && (val === "public" || val === "medicaid")) return true
                if (target === "self pay" && (val === "selfpay" || val.includes("self pay"))) return true
                if (target === "not reported" && (val.includes("not") && val.includes("reported"))) return true
                if (val === target) return true
                return false
            })

            const totals: HuddleTotals = {
                'Yes Huddle': { num: 0, denom: 0 },
                'No Huddle': { num: 0, denom: 0 }
            }

            typeRows.forEach((r: any[]) => {
                if (huddleIdx === -1) return
                const g = normalizeHuddleGroup(r[huddleIdx])
                if (!totals[g]) return

                const num = numeratorIdx >= 0 ? safeNumber(r[numeratorIdx]) : null
                const denom = denominatorIdx >= 0 ? safeNumber(r[denominatorIdx]) : null
                if (num !== null) totals[g].num += num
                if (denom !== null) totals[g].denom += denom
            })

            categories.push(displayInsuranceLabel(insType))

            const yesPct = totals['Yes Huddle'].denom > 0
                ? (totals['Yes Huddle'].num / totals['Yes Huddle'].denom) * 100
                : 0
            const noPct = totals['No Huddle'].denom > 0
                ? (totals['No Huddle'].num / totals['No Huddle'].denom) * 100
                : 0

            yesHuddleData.push(Math.round(yesPct))
            noHuddleData.push(Math.round(noPct))
            yesDenoms.push(totals['Yes Huddle'].denom)
            noDenoms.push(totals['No Huddle'].denom)
        })

        setChartCategories(categories)
        setChartSeries([
            { name: 'Yes Huddle', data: yesHuddleData },
            { name: 'No Huddle', data: noHuddleData }
        ])
        setNLabels({ yes: yesDenoms, no: noDenoms })

    }, [rawData, columnIndices, monthList, sliderRange, selectedQuestion, stratification, selectedInsuranceTypes])
    useEffect(() => {
            const checkScreenSize = () => {
            setIsMobile(window.innerWidth <= 768); 
            };

            checkScreenSize();

            window.addEventListener("resize", checkScreenSize);

            return () => {
            window.removeEventListener("resize", checkScreenSize);
            };
        }, []);
    useClickOutside([insuranceDropdownRef], () => setInsuranceDropdownOpen(false), insuranceDropdownOpen)
    useClickOutside([downloadMenuRef], () => setMenuOpen(false), menuOpen)

    const selectedQuestionData = questions.find(q => q.name === selectedQuestion)

    const isMaxInsuranceSelected = selectedInsuranceTypes.length >= BASE_MAX_INSURANCE_SELECTIONS

    if (initialLoading) {
        return <LoadingNotice title="Loading agreement data..." />
    }

    if (error) {
        return (
            <div className="agreement-insurance-page tmb-page">
                <div className="content-wrapper">
                    <div className="header-wrapper">
                        <span className="page-title title--fixed">
                            <span className="title-line">{PAGE_HEADING_LINE_1}</span>
                            <span className="title-line">{PAGE_HEADING_LINE_2}</span>
                        </span>
                    </div>
                    <div className="section-card" style={{ minHeight: 'auto' }}>
                        {error.includes('No data available') ? (
                            <div style={{ padding: '14px 12px', fontSize: '13px', fontWeight: 600, color: '#000000' }}>
                                No data available
                            </div>
                        ) : (
                            <p>Error: {error}</p>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div id={generatePDFIds.dashboard()} className="agreement-insurance-page tmb-page">
            <div className="content-wrapper">
                <div className="header-wrapper header--stratified">
                    <span className="page-title title--fixed">
                        <span className="title-line">{PAGE_HEADING_LINE_1}</span>
                        <span className="title-line">{PAGE_HEADING_LINE_2}</span>
                    </span>

                    <div className="controls-col controls--row">
                        <TmbControlsRow>
                            {globalMonthList.length > 0 && (
                                <DateRangeSlider
                                    monthList={globalMonthList}
                                    startIndex={sliderStartIdx}
                                    endIndex={sliderEndIdx}
                                    onChange={(start, end) => setSliderRange([globalMonthList[start]?.value || 0, globalMonthList[end]?.value || 0])}
                                />
                            )}

                            <TmbPillSelect
                                variant="multiselect"
                                displayText={
                                    selectedInsuranceTypes.length === 0 ? "Insurance" : (
                                        selectedInsuranceTypes.length === 1
                                            ? displayInsuranceLabel(selectedInsuranceTypes[0])
                                            : `${selectedInsuranceTypes.length} selected`
                                    )
                                }
                                style={{ minWidth: '100px' }}
                            >
                                <div className="insurance-dropdown">
                                    {availableInsuranceTypes.map(type => (
                                        <label
                                            key={type}
                                            className={`insurance-item-label ${!selectedInsuranceTypes.includes(type) && isMaxInsuranceSelected ? 'disabled' : ''}`}
                                        >
                                            <input
                                                type="checkbox"
                                                className="insurance-checkbox"
                                                checked={selectedInsuranceTypes.includes(type)}
                                                onChange={(e) => handleInsuranceToggle(type, e.target.checked)}
                                                disabled={!selectedInsuranceTypes.includes(type) && isMaxInsuranceSelected}
                                            />
                                            {displayInsuranceLabel(type)}
                                        </label>
                                    ))}
                                </div>
                            </TmbPillSelect>

                            <TmbStratificationPill value={stratification} onChange={setStratification} />

                            <TmbQuestionPill questions={questions} selectedQuestion={selectedQuestion} onSelect={setSelectedQuestion} />

                        </TmbControlsRow>
                    </div>
                </div>

                <div className="section-card" ref={chartContainerRef} style={!chartSeries.length || !chartCategories.length || !hasDataForRange ? { minHeight: 'auto' } : undefined}>
                    {loading ? (
                        <div style={{ padding: '14px 12px', fontSize: '13px', fontWeight: 600, color: '#000000' }}>
                            Loading…
                        </div>
                    ) : !chartSeries.length || !chartCategories.length || !hasDataForRange ? (
                        <div style={{ padding: '14px 12px', fontSize: '13px', fontWeight: 600, color: '#000000' }}>
                            No data available
                        </div>
                    ) : (<>
                    <div className="custom-download-menu" ref={downloadMenuRef}>
                        <div className="hamburger-icon" onClick={() => setMenuOpen(!menuOpen)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                <g>
                                    <rect x="4" y="6" width="16" height="2" rx="1" />
                                    <rect x="4" y="11" width="16" height="2" rx="1" />
                                    <rect x="4" y="16" width="16" height="2" rx="1" />
                                </g>
                            </svg>
                        </div>
                        {menuOpen && (
                            <div className="download-dropdown show">
                                <div className="menu-items-row">
                                    <div className="menu-item" onClick={async () => { await handleDownloadPNG(); setMenuOpen(false); }}>Download PNG</div>
                                    <div className="menu-item" onClick={async () => { await handleDownloadSVG(); setMenuOpen(false); }}>Download SVG</div>
                                    <div className="menu-item" onClick={() => { handleDownloadCSV(); setMenuOpen(false); }}>Download CSV</div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="chart-subtitle" id="agreement-question-text">
                        {selectedQuestionData?.fullText || 'Select a question'}
                    </div>

                    <div className="insurance-legend">
                        <div className="legend-item">
                            <span className="legend-dot no-huddle"></span>
                            <span>No Huddle</span>
                        </div>
                        <div className="legend-item">
                            <span className="legend-dot yes-huddle"></span>
                            <span>Yes Huddle</span>
                        </div>
                    </div>

                    <div className="chart-with-overlay">
                        {chartSeries[0]?.data?.length > 0 && (
                            <div className="insurance-column-headers">
                                {chartCategories.map((cat, idx) => (
                                    <div key={idx} className="column-header-item">
                                        {cat}
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="insurance-table-wrap" id="insurance-table-wrap">
                                <table className="insurance-bar-table">
                                    <colgroup>
                                        {chartCategories.map((_, i) => (
                                            <col key={i} style={{ width: `${100 / chartCategories.length}%` }} />
                                        ))}
                                    </colgroup>
                                    <tbody>
                                        <tr>
                                            {chartSeries[0]?.data.map((yesPct: number, i: number) => {
                                                const noPct = chartSeries[1]?.data[i] ?? 0
                                                const maxBarHeight = 320
                                                const yesHeight = Math.max(6, (yesPct / 100) * maxBarHeight)
                                                const noHeight = Math.max(6, (noPct / 100) * maxBarHeight)

                                                return (
                                                    <td key={i}>
                                                        <div className="bar-cell">
                                                            <div
                                                                className="bar-group"
                                                                onMouseEnter={(e) => {
                                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                                    setHoverTooltip({
                                                                        visible: true,
                                                                        x: !isMobile? rect.right + 10: rect.right/2,
                                                                        y: rect.top + (rect.height / 2) - 30,
                                                                        content: (
                                                                            <div style={{ boxShadow: '2px 2px 6px -4px #999', cursor: 'default', zIndex: 9999, border: '1px solid #e3e3e3', backgroundColor: '#fff', minWidth: '150px', borderRadius: '5px', overflow: 'hidden' }}>
                                                                                <div className="apexcharts-tooltip-title" style={{ backgroundColor: '#ECEFF1', fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', borderBottom: '1px solid #ddd', padding: '8px 10px', margin: 0, color: '#000' }}>
                                                                                    No Huddle {nLabels.no[i] ?? 0}
                                                                                </div>
                                                                                <div style={{ padding: '8px 10px', backgroundColor: '#fff', display: 'flex', alignItems: 'center' }}>
                                                                                    <span style={{ backgroundColor: '#D79852', width: '10px', height: '10px', display: 'inline-block', borderRadius: '50%', marginRight: '8px' }}></span>
                                                                                    <span style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', color: '#000', marginRight: '5px' }}>Strongly Agree: </span>
                                                                                    <span style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', fontWeight: 600, color: '#000' }}>
                                                                                        {formatPercentLabel(noPct)}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        )
                                                                    });
                                                                }}
                                                                onMouseLeave={() => setHoverTooltip(null)}
                                                            >
                                                                <div className="bar-value">{formatPercentLabel(noPct)}</div>
                                                                <div
                                                                    className="bar-fill no-huddle"
                                                                    style={{ height: `${noHeight}px` }}
                                                                />
                                                            </div>
                                                            <div
                                                                className="bar-group"
                                                                onMouseEnter={(e) => {
                                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                                    setHoverTooltip({
                                                                        visible: true,
                                                                        x: !isMobile? rect.right + 10: rect.right/2,
                                                                        y: rect.top + (rect.height / 2) - 30,
                                                                        content: (
                                                                            <div style={{ boxShadow: '2px 2px 6px -4px #999', cursor: 'default', zIndex: 9999, border: '1px solid #e3e3e3', backgroundColor: '#fff', minWidth: '150px', borderRadius: '5px', overflow: 'hidden' }}>
                                                                                <div className="apexcharts-tooltip-title" style={{ backgroundColor: '#ECEFF1', fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', borderBottom: '1px solid #ddd', padding: '8px 10px', margin: 0, color: '#000' }}>
                                                                                    Yes Huddle {nLabels.yes[i] ?? 0}
                                                                                </div>
                                                                                <div style={{ padding: '8px 10px', backgroundColor: '#fff', display: 'flex', alignItems: 'center' }}>
                                                                                    <span style={{ backgroundColor: '#3F548D', width: '10px', height: '10px', display: 'inline-block', borderRadius: '50%', marginRight: '8px' }}></span>
                                                                                    <span style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', color: '#000', marginRight: '5px' }}>Strongly Agree: </span>
                                                                                    <span style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', fontWeight: 600, color: '#000' }}>
                                                                                        {formatPercentLabel(yesPct)}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        )
                                                                    });
                                                                }}
                                                                onMouseLeave={() => setHoverTooltip(null)}
                                                            >
                                                                <div className="bar-value">{formatPercentLabel(yesPct)}</div>
                                                                <div
                                                                    className="bar-fill yes-huddle"
                                                                    style={{ height: `${yesHeight}px` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </td>
                                                )
                                            })}
                                        </tr>
                                    </tbody>
                                </table>
                        </div>

                        {chartSeries[0]?.data?.length > 0 && (
                            <div className="insurance-n-values-row">
                                <span className="n-label">N=</span>
                                {chartCategories.map((_, idx) => (
                                    <div key={idx} className="n-value-column">
                                        <span className="n-value no">{nLabels.no[idx] ?? 0}</span>
                                        <span className="n-value yes">{nLabels.yes[idx] ?? 0}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    </>)}
                </div>
            </div>
            {hoverTooltip && hoverTooltip.visible && (
                <div style={{
                    position: 'fixed',
                    top: hoverTooltip.y,
                    left: hoverTooltip.x,
                    zIndex: 10000,
                    pointerEvents: 'none',
                    transform: 'translateY(-50%)'
                }}>
                    {hoverTooltip.content}
                </div>
            )}
        </div>
    )
}

export default AgreementInsurance
